import { useMemo } from "react";
import { Input } from "../../components/ui/input";
import { Plus, Trash, Clock } from "@phosphor-icons/react";

/**
 * Sprint H Batch 4 — Designer de horarios por día con tramos partido/intensivo.
 *
 * Props:
 *  - value: { "0": [{start,end}, ...], "1": [...] }   // 0=Lun, 6=Dom
 *  - workingDays: [0,1,2,3,4,5]   (legacy compat — días laborables)
 *  - fallbackStart/fallbackEnd: "HH:MM" usado al activar un nuevo día
 *  - onChange(perDay, workingDays)
 *
 * UX:
 *  - Lista de 7 días (Lun-Dom). Checkbox "abierto" por día.
 *  - Si cerrado → no se muestra ningún tramo.
 *  - Si abierto → tramo 1 (Mañana) obligatorio, botón +Tramo añade tramo 2 (Tarde).
 *  - Botón eliminar por tramo (mín 1 tramo si abierto).
 *  - Botones rápidos "Intensivo" (1 tramo 09-15) y "Partido" (09-14 + 16-20).
 *  - Validación inline: end > start; tramo 2 start ≥ tramo 1 end.
 */
const DAYS = [
  { idx: 0, short: "Lun", long: "Lunes" },
  { idx: 1, short: "Mar", long: "Martes" },
  { idx: 2, short: "Mié", long: "Miércoles" },
  { idx: 3, short: "Jue", long: "Jueves" },
  { idx: 4, short: "Vie", long: "Viernes" },
  { idx: 5, short: "Sáb", long: "Sábado" },
  { idx: 6, short: "Dom", long: "Domingo" },
];

const INTENSIVO = [{ start: "09:00", end: "15:00" }];
const PARTIDO = [
  { start: "09:00", end: "14:00" },
  { start: "16:00", end: "20:00" },
];

export default function OpeningHoursDesigner({ value, workingDays, fallbackStart, fallbackEnd, onChange }) {
  const perDay = value || {};

  const isOpen = (idx) => {
    const day = perDay[String(idx)];
    if (Array.isArray(day) && day.length > 0) return true;
    // Legacy: si no hay tramos pero el día está en workingDays, considerarlo abierto
    // (con fallback start/end). Esto activa la migración suave al primer guardado.
    return false;
  };

  const tramosOf = (idx) => perDay[String(idx)] || [];

  const setDay = (idx, tramos) => {
    const next = { ...perDay };
    if (!tramos || tramos.length === 0) {
      delete next[String(idx)];
    } else {
      next[String(idx)] = tramos;
    }
    const wd = Object.keys(next).map((k) => parseInt(k, 10)).filter((n) => !isNaN(n)).sort();
    onChange(next, wd.length ? wd : workingDays);
  };

  const toggleOpen = (idx) => {
    if (isOpen(idx)) setDay(idx, []);
    else setDay(idx, [{ start: fallbackStart || "09:00", end: fallbackEnd || "20:00" }]);
  };

  const addTramo = (idx) => {
    const cur = tramosOf(idx);
    if (cur.length >= 3) return;
    setDay(idx, [...cur, { start: "16:00", end: "20:00" }]);
  };

  const removeTramo = (idx, tramoIdx) => {
    const cur = tramosOf(idx);
    const next = cur.filter((_, i) => i !== tramoIdx);
    setDay(idx, next);
  };

  const updateTramo = (idx, tramoIdx, field, val) => {
    const cur = tramosOf(idx).map((t, i) => i === tramoIdx ? { ...t, [field]: val } : t);
    setDay(idx, cur);
  };

  const applyPreset = (idx, preset) => setDay(idx, JSON.parse(JSON.stringify(preset)));
  const applyToAll = (preset) => {
    const next = {};
    for (let i = 0; i < 5; i++) next[String(i)] = JSON.parse(JSON.stringify(preset));
    const wd = [0, 1, 2, 3, 4];
    onChange(next, wd);
  };

  const summary = useMemo(() => {
    const open = Object.keys(perDay).length;
    return open
      ? `${open} día${open === 1 ? "" : "s"} configurado${open === 1 ? "" : "s"}`
      : "Sin horario definido — se usará el rango legacy.";
  }, [perDay]);

  return (
    <div className="space-y-3" data-testid="hours-designer">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Label />
        <div className="flex items-center gap-3">
          <Clock size={14} weight="duotone" className="text-[var(--ag-aqua)]" />
          <span className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)] font-semibold">Horarios de apertura</span>
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          <button type="button" onClick={() => applyToAll(INTENSIVO)}
            className="px-2 py-1 rounded-sm border border-[var(--ag-border)] hover:border-[var(--ag-gold)] text-[var(--ag-text-dim)]"
            data-testid="hd-apply-intensive">
            L–V intensivo 9–15
          </button>
          <button type="button" onClick={() => applyToAll(PARTIDO)}
            className="px-2 py-1 rounded-sm border border-[var(--ag-border)] hover:border-[var(--ag-gold)] text-[var(--ag-text-dim)]"
            data-testid="hd-apply-split">
            L–V partido 9–14 / 16–20
          </button>
        </div>
      </div>

      <div className="ag-card divide-y divide-[var(--ag-border)] overflow-hidden">
        {DAYS.map((d) => {
          const open = isOpen(d.idx);
          const tramos = tramosOf(d.idx);
          return (
            <div key={d.idx} className="p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3" data-testid={`hd-day-${d.idx}`}>
              <label className="flex items-center gap-2 min-w-[100px] cursor-pointer">
                <input type="checkbox" checked={open} onChange={() => toggleOpen(d.idx)}
                  className="accent-[var(--ag-aqua)] h-4 w-4" data-testid={`hd-toggle-${d.idx}`} />
                <span className="text-sm font-semibold w-12">{d.short}</span>
                <span className="hidden sm:inline text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">{d.long}</span>
              </label>
              {!open && (
                <span className="text-xs text-[var(--ag-text-dim)] italic">Cerrado</span>
              )}
              {open && (
                <div className="flex-1 flex flex-wrap items-center gap-3">
                  {tramos.map((tr, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-1.5 bg-[var(--ag-midnight)]/50 px-2 py-1 rounded-sm border border-[var(--ag-border)] w-full sm:w-auto" data-testid={`hd-tramo-${d.idx}-${i}`}>
                      <span className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)] shrink-0 w-14 sm:w-auto">{i === 0 ? "Mañana" : i === 1 ? "Tarde" : `T${i + 1}`}</span>
                      <Input type="time" value={tr.start || ""} onChange={(e) => updateTramo(d.idx, i, "start", e.target.value)}
                        className="rounded-sm tabular-nums h-9 w-[108px] text-sm" data-testid={`hd-tramo-${d.idx}-${i}-start`} />
                      <span className="text-[var(--ag-text-dim)]">–</span>
                      <Input type="time" value={tr.end || ""} onChange={(e) => updateTramo(d.idx, i, "end", e.target.value)}
                        className="rounded-sm tabular-nums h-9 w-[108px] text-sm" data-testid={`hd-tramo-${d.idx}-${i}-end`} />
                      {tramos.length > 1 && (
                        <button type="button" onClick={() => removeTramo(d.idx, i)}
                          className="text-[var(--ag-text-dim)] hover:text-red-400 ml-auto sm:ml-1" data-testid={`hd-tramo-${d.idx}-${i}-remove`}>
                          <Trash size={14} weight="bold" />
                        </button>
                      )}
                    </div>
                  ))}
                  {tramos.length < 3 && (
                    <button type="button" onClick={() => addTramo(d.idx)}
                      className="text-[11px] uppercase tracking-wider px-2 py-1 rounded-sm border border-dashed border-[var(--ag-border)] hover:border-[var(--ag-gold)] text-[var(--ag-text-dim)] inline-flex items-center gap-1"
                      data-testid={`hd-add-tramo-${d.idx}`}>
                      <Plus size={11} weight="bold" /> Tramo
                    </button>
                  )}
                  <div className="flex items-center gap-1 ml-auto">
                    <button type="button" onClick={() => applyPreset(d.idx, INTENSIVO)}
                      className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm border border-[var(--ag-border)] hover:border-[var(--ag-gold)] text-[var(--ag-text-dim)]"
                      title="Aplicar horario intensivo (9–15)" data-testid={`hd-preset-intensive-${d.idx}`}>
                      Intensivo
                    </button>
                    <button type="button" onClick={() => applyPreset(d.idx, PARTIDO)}
                      className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm border border-[var(--ag-border)] hover:border-[var(--ag-gold)] text-[var(--ag-text-dim)]"
                      title="Aplicar horario partido (9–14 + 16–20)" data-testid={`hd-preset-split-${d.idx}`}>
                      Partido
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-[var(--ag-text-dim)] uppercase tracking-wider">{summary}</p>
    </div>
  );
}

function Label() { return null; }
